<template>
  <div class="scenario-container muzik-performer mk-container">
    <Breadcrumbs :breadcrumbs="breadcrumbs" />
    <div v-if="loaded && errorStatus">
      <NotFoundDummy :class-name="'abreast'"
                     :title="$t('DUMMY.NOT_FOUND_TITLE')"
                     :desp="$t('DUMMY.NOT_FOUND_DESP')"
      />
    </div>
    <div v-else-if="loaded">
      <LayoutInnerPage v-if="performerData && performerData.i18lang">
        <template slot="left">
          <PeopleInfo :img-src="performerData.file_link"
                      :title="performerName"
                      :alias-title="$t('COMPOSER.COMPOSER_ALIAS')"
                      :alias="performerData.i18lang[locale].alias"
          />
          <Paganiniplus />
        </template>
        <template slot="right">
          <PeopleDesp :content="performerData.i18lang && performerData.i18lang[locale] && performerData.i18lang[locale].description ? performerData.i18lang[locale].description : '-'" />
          <PerformerAccordion v-if="performerWorkData"
                              :content="performerWorkData"
                              :title="$t('PERFORMER.ALL_VERSIONS')"
                              type="performerWork"
                              @play-track-list="playTrackList"
          />
        </template>
      </LayoutInnerPage>
      <CommonDummy v-else
                   :title="$t('DUMMY.LISTEN_COMMON')"
      />
    </div>
    <div v-else class="loader-container">
      <Loader />
    </div>
  </div>
</template>

<script>
import Breadcrumbs from '@/components/common/Breadcrumbs'
import CommonDummy from '@/components/common/DummyStatus/CommonDummy'
import NotFoundDummy from '@/components/common/DummyStatus/NotFoundDummy'
import Loader from '@/components/common/Loader'
import Paganiniplus from '@/components/tsp/Paganiniplus'
import LayoutInnerPage from '@/components/people/LayoutInnerPage'
import PeopleInfo from '@/components/people/PeopleInfo'
import PeopleDesp from '@/components/people/PeopleDesp'
import PerformerAccordion from '@/components/people/PerformerAccordion'
import PeopleService from '@/assets/services/PeopleService'
import WorkService from '@/assets/services/WorkService'
import TrackService from '@/assets/services/TrackService'
import { displayPerformerName, displayPerformerDescription } from '@/assets/helpers/PerformerHelper'

export default {
  name: 'PerformerById',
  components: {
    Paganiniplus,
    Breadcrumbs,
    CommonDummy,
    NotFoundDummy,
    Loader,
    LayoutInnerPage,
    PeopleInfo,
    PeopleDesp,
    PerformerAccordion
  },
  data() {
    return {
      performerData: {},
      performerWorkData: {},
      loaded: false,
      errorStatus: false
    }
  },
  metaInfo() {
    return {
      title: this.title,
      meta: [
        // ...this.$route.meta.metaInfo.meta,
        { vmid: 'name', name: 'name', content: this.title },
        { vmid: 'description', name: 'description', content: this.description },
        { vmid: 'keywords', name: 'keywords', content: this.keywords },
        { vmid: 'image', name: 'image', content: this.cover },
        { vmid: 'og:title', name: 'og:title', content: this.title },
        { vmid: 'og:description', name: 'og:description', content: this.description },
        { vmid: 'og:image', name: 'og:image', content: this.cover }
      ],
      link: [
        { vmid: 'canonical', rel: 'canonical', href: this.canonicalURL },
        { vmid: 'image_src', rel: 'image_src', href: this.cover }
      ],
      __dangerouslyDisableSanitizers: ['script'],
      script: [{ innerHTML: JSON.stringify(this.structuredData), type: 'application/ld+json' }]
    }
  },
  computed: {
    breadcrumbs() {
      let breadcrumbsAry = []
      const indexBreadcrumbs = { routeName: 'index', localeLabel: 'LISTEN.TITLE', redirectPath: '' }
      const performerListBreadcrumbs = { routeName: '', localeLabel: 'PERFORMER.INDEX.TITLE', redirectPath: '' }
      breadcrumbsAry.push(indexBreadcrumbs)
      breadcrumbsAry.push(performerListBreadcrumbs)
      if (this.performerName) {
        const performerBreadcrumbs = {
          routeName: 'performerById',
          routeParams: {
            id: this.performerData.id
          },
          localeLabel: this.performerName || 'COMMON.PERFORER',
          redirectPath: ''
        }
        breadcrumbsAry.push(performerBreadcrumbs)
      }
      return breadcrumbsAry
    },
    locale() {
      return this.$i18n.locale.toLowerCase()
    },
    performerName() {
      return this.displayPerformerName(this.performerData)
    },
    initialClick() {
      return this.$store.state.player.initialClick
    },
    // default meta data, update after fetched composer
    title() {
      const postfixTitle = `${this.$t('LISTEN.TITLE')} | MUZIK AIR`
      return this.performerData && this.performerName
        ? `${this.performerName} | ${postfixTitle}`
        : `${this.$t('COMMON.PERFORMER')} | ${postfixTitle}`
    },
    description() {
      const performerdescription = this.displayPerformerDescription(this.performerData).slice(0, 200)
      return this.performerData && performerdescription
        ? `${performerdescription}` || this.$t('COMMON.PERFORMER')
        : this.$t('COMMON.PERFORMER')
    },
    keywords() {
      let keywordsAry = []
      keywordsAry.push(this.$t('COMMON.PERFORMER'))
      keywordsAry.push(this.performerName)
      if (this.performerWorkData) {
        //
      }
      return keywordsAry.join(', ')
    },
    cover() {
      return this.performerData && this.performerData.file_link
        ? this.performerData.file_link
        : `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/img/cover-social/listen.png`
    },
    canonicalURL() {
      return this.performerData && this.performerData.id
        ? `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/performers/${this.performerData.id}`
        : `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}`
    },
    structuredData() {
      return [
        {
          '@context': 'http://schema.org',
          '@type': 'MusicGroup',
          image: this.cover,
          name: this.performer && this.displayPerformerName(this.performer),
          url: this.canonicalURL,
          description: this.description
        },
        {
          '@context': 'http://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              item: {
                '@id': `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/`,
                name: this.$t('LISTEN.TITLE')
              }
            },
            {
              '@type': 'ListItem',
              position: 2,
              item: {
                '@id': `${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/performers`,
                name: this.$t('PERFORMER.INDEX.TITLE')
              }
            }
          ]
        }
      ]
    }
  },
  mounted() {
    this.getPerformer()
  },
  methods: {
    displayPerformerName,
    displayPerformerDescription,
    parseRewriteURL(unparsedIdStr) {
      const MuzikListenV4Reg = /^[A-F\d]{8}-[A-F\d]{4}-4[A-F\d]{3}-[89AB][A-F\d]{3}-[A-F\d]{12}/gi
      const MuzikListenV3Reg = /^[A-Z\d]{6}/gi
      let parsedIdStr = ''
      if (MuzikListenV4Reg.test(unparsedIdStr)) {
        parsedIdStr = unparsedIdStr.match(MuzikListenV4Reg)[0] // MUZIK LISTEN v4 (UUID.v4) id format
      } else if (MuzikListenV3Reg.test(unparsedIdStr)) {
        parsedIdStr = unparsedIdStr.match(MuzikListenV3Reg)[0] // MUZIK LISTEN v3 id format
      } else {
        parsedIdStr = unparsedIdStr
      }
      return parsedIdStr
    },
    async getPerformer() {
      this.loaded = false
      const unparsedIdStr = this.$route.params.id.replace('.html', '') // external share link
      const performerId = this.parseRewriteURL(unparsedIdStr)
      const performerRewrite = unparsedIdStr.replace(performerId, '')
      // const performerId = this.$route.params.id
      let res = await PeopleService.getPeople(performerId)
      if (res !== null) {
        res.i18lang = {}
        res.langs.forEach(item => {
          res.i18lang[item.locale] = item
          res.i18lang[item.locale].alias = ''
          item.aliases.forEach(item => {
            res.i18lang[item.locale].alias = res.i18lang[item.locale].alias + item.first_name + ' ' + item.middle_name + ' ' + item.last_name
          })
        })
        // let res2 = await WorkService.getWorkList({'movements': {'tracks': {'people': {'id': [performerId]}}}})
        let res2 = await WorkService.getWorkList({ people_id: performerId, list: 'performer' })
        if (res2) {
          let tmpWorkData = {}
          let trackIndex = 0
          res2.data.forEach(item => {
            item.movements.forEach((movement) => {
              if (movement.tracks && movement.tracks.length) {
                movement.tracks.forEach((track, index) => {
                  if (!tmpWorkData[item.people_id]) {
                    tmpWorkData[item.people_id] = {}
                    tmpWorkData[item.people_id].langs = item.people.langs
                    tmpWorkData[item.people_id].works = {}
                  }
                  if (!tmpWorkData[item.people_id].works[item.id]) {
                    tmpWorkData[item.people_id].works[item.id] = {}
                    tmpWorkData[item.people_id].works[item.id].id = item.id
                    tmpWorkData[item.people_id].works[item.id].langs = item.langs
                  }
                  if (!tmpWorkData[item.people_id].works[item.id].movement) {
                    tmpWorkData[item.people_id].works[item.id].movement = {}
                    tmpWorkData[item.people_id].works[item.id].movement.id = movement.id
                    // tmpWorkData[item.people_id].works[item.id].movement.langs = movement.langs
                    tmpWorkData[item.people_id].works[item.id].movement.tracks = []
                    trackIndex = 0
                  }
                  trackIndex = tmpWorkData[item.people_id].works[item.id].movement.tracks.length
                  tmpWorkData[item.people_id].works[item.id].movement.tracks[trackIndex] = {}
                  tmpWorkData[item.people_id].works[item.id].movement.tracks[trackIndex] = track
                  tmpWorkData[item.people_id].works[item.id].movement.tracks[trackIndex].langs = movement.langs
                })
              }
            })
          })
          this.performerData = res
          this.performerWorkData = tmpWorkData
          this.loaded = true
        } else {
          this.performerData = {}
          this.loaded = true
          this.errorStatus = true
        }
      } else {
        if (performerRewrite) {
          this.loaded = true
          const queryKeyword = performerRewrite.split('-').join(' ')
          this.$router.push({
            name: 'search',
            query: {
              type: 'performer',
              q: queryKeyword
            }
          })
        } else {
          this.loaded = true
        }
      }
    },
    async playTrackList(tracks) {
      if (!this.initialClick) {
        // Trigger click-to-play policy
        document.querySelector('#audioPlayer').play()
        document.querySelector('#audioPlayer').pause()
      }
      let trackIdArray = []
      tracks.forEach(track => {
        trackIdArray.push(track.id)
      })
      let res = await TrackService.getTracks({ id: trackIdArray })
      let playlist = []
      playlist = trackIdArray.map(trackId => {
        return res.data.find(item => item.id === trackId)
      }).filter(track => track) // filter undefinded obj
      this.$store.commit('TOGGLE_PLAYER_RANDOM', false)
      this.$store.commit('RESET_QUEUE')
      this.$store.commit('SET_PLAYLIST_TO_QUEUE', playlist)
      this.$store.commit('SET_PLAYER_INDEX', 0)
      this.$store.commit('TOGGLE_PLAYER_PAUSED', false)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';

.muzik-performer {
  // padding: 0 $padding-lg-grid-x1 66px;
  padding-bottom: 66px;
  .breadcrumbs{
    padding: 24px 0;
  }
  .people-info{
    margin-bottom: 32px;
  }
  .dummy{
    text-align: left;
    margin-bottom: 60px;
  }
  .loader-container{
    text-align: center;
    .loader{
      margin-bottom: 40px;
    }
  }
}

@media only screen and (min-width: 1440px) {
  // default
}

@media only screen and (max-width: 1439.98px) {
  .muzik-performer {
    // padding: 0 $padding-md-grid-x1;
    padding-bottom: 66px;
  }
}

@media only screen and (max-width: 1023.98px) {
  .muzik-performer {
    // padding: 0 $padding-sm-grid-x1;
    padding-bottom: 66px;
    .dummy{
      display: none;
    }
  }
}

@media only screen and (max-width: 767.98px) {
  .muzik-performer {
    padding-bottom: 66px;
  }
}

</style>
